<!--
📌 Componente: AlertModal.vue
📂 Caminho: src/components/AlertModal.vue
📝 Descrição: Modal de alerta e confirmação de ações, permitindo exibir mensagens
    personalizadas com botões de confirmação e cancelamento.

👁️ Interface e Interação com o Usuário:
  - Modal centralizado na tela para mensagens importantes.
  - Título exibido em destaque.
  - Mensagem customizável (texto puro ou HTML).
  - Botão de confirmação com texto configurável.
  - Botão de cancelamento opcional, também configurável.

📥 Entrada (Props):
  - `title` (String) - Título do modal (padrão: "Achtung").
  - `message` (String, obrigatório) - Texto exibido no modal.
  - `renderHtml` (Boolean) - Renderiza a mensagem como HTML (padrão: false).
  - `confirmText` (String) - Texto do botão de confirmar (padrão: "Bestätigen").
  - `cancelText` (String) - Texto do botão de cancelar (padrão: "Abbrechen").
  - `showCancel` (Boolean) - Exibe ou oculta o botão de cancelar (padrão: true).

📤 Saída (Emits):
  - `@confirm` - Quando o botão de confirmação é clicado.
  - `@cancel` - Quando o botão de cancelamento é clicado.

🔗 Dependências: Nenhuma.
-->

<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 overflow-y-auto bg-cinza-900/60 backdrop-blur-sm p-4 text-center"
    style="
      z-index: 150;
      padding-top: calc(1rem + env(safe-area-inset-top, 0px));
      padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    "
  >
    <!-- Elemento fantasma para centralizar o modal verticalmente em telas sm/maiores -->
    <span
      class="hidden sm:inline-block sm:h-screen sm:align-middle"
      aria-hidden="true"
      >&#8203;</span
    >

    <div
      class="inline-block bg-branco rounded shadow-md max-w-md sm:max-w-lg w-full p-5 sm:p-7 text-center border border-cinza-claro/50 transition-all duration-300 my-4 sm:my-8 sm:align-middle"
    >
      <!-- Título -->
      <h2 class="text-lg font-semibold mb-4">{{ displayTitle }}</h2>

      <!-- Mensagem -->
      <p v-if="!renderHtml" class="text-cinza-700 mb-6">
        {{ localMessage || message }}
      </p>
      <div
        v-else
        class="text-cinza-700 mb-6"
        v-html="localMessage || message"
      ></div>

      <!-- Botões -->
      <div class="flex justify-center space-x-4">
        <button
          v-if="showCancel"
          @click="onCancel"
          class="bg-vermelho-suave text-branco px-4 py-2 rounded hover:bg-vermelho-suave/90"
        >
          {{ displayCancelText }}
        </button>
        <button
          @click="onConfirm"
          class="bg-verde-suave text-branco px-4 py-2 rounded hover:bg-verde-suave/90"
        >
          {{ displayConfirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: { type: String, default: "Achtung" }, // Título do modal
    message: { type: String, required: true }, // Mensagem do modal
    renderHtml: { type: Boolean, default: false }, // Controle para renderizar HTML ou texto
    confirmText: { type: String, default: "Bestätigen" }, // Texto do botão de confirmar
    cancelText: { type: String, default: "Abbrechen" }, // Texto do botão de cancelar
    showCancel: { type: Boolean, default: true }, // Controle de exibição do botão de cancelar
  },
  data() {
    return {
      isVisible: false,
      localTitle: null,
      localMessage: null,
    }; // Controle de visibilidade do modal
  },
  computed: {
    displayTitle() {
      return this.localTitle ?? this.title ?? this.$t("confirm.title");
    },
    displayConfirmText() {
      return this.confirmText ?? this.$t("buttons.confirm");
    },
    displayCancelText() {
      return this.cancelText ?? this.$t("buttons.cancel");
    },
  },
  methods: {
    show() {
      this.isVisible = true;
    },
    openModal({ title, message }) {
      this.localTitle = title;
      this.localMessage = message;
      this.isVisible = true;
    },
    hide() {
      this.isVisible = false;
      this.localTitle = null; // Reset ao fechar
      this.localMessage = null;
    },
    onConfirm() {
      this.$emit("confirm"); // Emite o evento de confirmação
      this.hide();
    },
    onCancel() {
      this.$emit("cancel"); // Emite o evento de cancelamento
      this.hide();
    },
  },
};
</script>
